import { VersesList } from '../verses/VersesList';
import type { VersesListProps } from '../verses/VersesList';
import { useLang } from '../i18n/lang';

/*
 * TWO PASSAGES, READ SIDE BY SIDE.
 *
 * One surface for both things this project sets beside each other: the same episode in the Bible and
 * the Quran, and a chapter told twice inside one scripture. Two columns, each the same passage list
 * the drawer uses — so a verse reads identically and its names stay doors. What the pair is, and what
 * it does and does not claim, is the caller's caption; this only lays them out. That caveat is
 * load-bearing everywhere it is used: this project shows the pairing, it does not adjudicate it.
 */
export interface SideBySideProps {
  title: string;
  leftLabel: string;
  rightLabel: string;
  left: VersesListProps;
  right: VersesListProps;
  /** A note under the two columns — why they are paired, and what the pairing does not claim. */
  caption?: string;
  onJump?: (id: string) => void;
  onClose: () => void;
}

export function SideBySide(
  { title, leftLabel, rightLabel, left, right, caption, onJump, onClose }: SideBySideProps,
): React.JSX.Element {
  const { t } = useLang();
  return (
    <aside className="story" role="region" aria-label={title}>
      <div className="story-head">
        <h2 className="story-title">{title}</h2>
        <button type="button" className="story-close" onClick={onClose} aria-label={t('action.close')}>×</button>
      </div>

      <div className="story-cols">
        <section className="story-col story-col--left">
          <h3 className="story-range">{leftLabel}</h3>
          <VersesList {...left} onJump={onJump} />
        </section>
        <section className="story-col story-col--right">
          <h3 className="story-range">{rightLabel}</h3>
          <VersesList {...right} onJump={onJump} />
        </section>
      </div>

      {caption && <p className="story-why">{caption}</p>}
    </aside>
  );
}
